import { useEffect, useState, type FormEvent } from 'react'
import { Link, useLocation, useNavigate } from 'react-router-dom'
import { esquemaEntrar } from '@compranorte/contracts'
import { useEntrar } from '../api/hooks'
import { useSesion } from '../auth/sesion'
import { MarcoAcceso } from '../componentes/MarcoAcceso'
import { Aviso } from '../componentes/ui'

export function Login() {
  const { yo } = useSesion()
  const entrar = useEntrar()
  const navigate = useNavigate()
  const lugar = useLocation()
  // A dónde quería ir antes de que lo mandáramos a entrar.
  const destino = (lugar.state as { volverA?: string } | null)?.volverA ?? '/catalogo'

  const [email, setEmail] = useState('')
  const [clave, setClave] = useState('')
  const [campos, setCampos] = useState<Record<string, string>>({})

  useEffect(() => {
    if (yo) navigate(destino, { replace: true })
  }, [yo, destino, navigate])

  function enviar(ev: FormEvent) {
    ev.preventDefault()
    if (entrar.isPending) return

    /*
     * Se valida con el MISMO esquema que usa el servidor, importado de
     * `@compranorte/contracts`. No es para ahorrarle trabajo a la API —que
     * valida igual— sino para que el formato del correo se marque en el campo
     * antes del viaje, con exactamente el criterio que va a aplicar el backend.
     */
    const revision = esquemaEntrar.safeParse({ email, clave })
    if (!revision.success) {
      const errores: Record<string, string> = {}
      for (const i of revision.error.issues) {
        const campo = i.path[0]
        if (typeof campo === 'string' && !errores[campo]) errores[campo] = i.message
      }
      setCampos(errores)
      return
    }

    setCampos({})
    entrar.mutate(revision.data)
    // La navegación la hace el efecto de arriba, en cuanto el perfil está.
  }

  const enVuelo = entrar.isPending

  return (
    <MarcoAcceso
      pie={
        <Link to="/catalogo" className="text-[12px] text-acero-500 hover:text-acero-800">
          ← Ver el catálogo sin entrar
        </Link>
      }
    >
      <h1 className="text-[17px] font-semibold text-acero-900">Entrar</h1>
      <p className="text-[12.5px] text-acero-600 mt-1 leading-relaxed">
        Con su cuenta de cliente, de proveedor o del equipo de Compranorte.
      </p>

      <form onSubmit={enviar} noValidate className="mt-4 space-y-3">
        {entrar.error && (
          <Aviso tono="error">
            <span role="alert">{entrar.error.message}</span>
            {/*
              Un 429 no se arregla insistiendo, y el mensaje del backend ya dice
              cuántos minutos hay que esperar; acá solo se le da el camino
              alternativo por si la contraseña de verdad se perdió.
            */}
            {entrar.error.estado === 429 && (
              <>
                {' '}
                <Link to="/recuperar" className="text-marca-600 underline hover:no-underline">
                  Cree una contraseña nueva
                </Link>{' '}
                en vez de seguir probando.
              </>
            )}
          </Aviso>
        )}

        <label className="block">
          <span className="etiqueta">Correo</span>
          <input
            className={`campo ${campos['email'] ? 'campo-error' : ''}`}
            type="email"
            name="email"
            autoComplete="username"
            autoFocus
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            disabled={enVuelo}
          />
          {campos['email'] && <p className="error-msg">{campos['email']}</p>}
        </label>

        <label className="block">
          <span className="etiqueta">Contraseña</span>
          <input
            className={`campo ${campos['clave'] ? 'campo-error' : ''}`}
            type="password"
            name="password"
            autoComplete="current-password"
            value={clave}
            onChange={(e) => setClave(e.target.value)}
            disabled={enVuelo}
          />
          {campos['clave'] && <p className="error-msg">{campos['clave']}</p>}
        </label>

        <button type="submit" className="btn btn-accion w-full" disabled={enVuelo}>
          {enVuelo ? 'Entrando…' : 'Entrar'}
        </button>
      </form>

      {/*
        Enlace real, no una instrucción. La v1 decía "pídale a un administrador
        que la restablezca" y no existía forma de hacerlo: quien perdía la
        contraseña quedaba fuera para siempre.
      */}
      <p className="ayuda mt-3 text-center">
        <Link to="/recuperar" className="text-marca-600 hover:underline">
          Olvidé mi contraseña
        </Link>
      </p>

      <p className="ayuda mt-2 text-center">
        ¿No tiene cuenta?{' '}
        <Link to="/registro" className="text-marca-600 hover:underline">
          Cree una
        </Link>
      </p>
    </MarcoAcceso>
  )
}
